import React, { useEffect, useMemo, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { checkLicencie } from "../../services/licenciesServices";
import Button from "../../components/Button/Button";

const DEFAULT_SAISON = "2025-2026";

const normalizeLicence = (txt = "") =>
  String(txt).trim().toUpperCase().replace(/\s+/g, "");

export default function ScanbarPage() {
  const [season, setSeason] = useState(DEFAULT_SAISON);

  // Résultat de la vérif (cartouche vert/rouge)
  const [result, setResult] = useState(null); // {found, saison_ok, message, licencie?}
  const [checking, setChecking] = useState(false);

  // Saisie manuelle
  const [manualCode, setManualCode] = useState("");

  // Modale scanner
  const [open, setOpen] = useState(false);
  const [modalKey, setModalKey] = useState(0); // force remount du <video> à chaque session

  // Caméras
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");

  // ZXing
  const codeReaderRef = useRef(null);
  const controlsRef = useRef(null);
  const videoRef = useRef(null);

  // Anti-doublon et cooldown
  const lastCodeRef = useRef("");
  const cooldownRef = useRef(false);
  const cooldownMs = 900;

  // Beep
  const audioCtxRef = useRef(null);
  const playBeep = () => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "square";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + 0.01);
      osc.start();
      setTimeout(() => {
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.12);
        osc.stop(ctx.currentTime + 0.14);
      }, 60);
    } catch {}
  };

  /* =========================
     Chargement des caméras
     ========================= */
  useEffect(() => {
    (async () => {
      try {
        const rdr = new BrowserMultiFormatReader();
        codeReaderRef.current = rdr;
        const list = await BrowserMultiFormatReader.listVideoInputDevices();
        setDevices(list || []);
        // Choisir de préférence la caméra arrière
        const preferred =
          list.find((d) =>
            /back|rear|environment|arrière/i.test(d.label || "")
          ) || list[0];
        setDeviceId(preferred?.deviceId || "");
      } catch (e) {
        console.error("Erreur enumerate devices:", e);
      }
    })();

    return () => {
      try {
        audioCtxRef.current?.close?.();
      } catch {}
    };
  }, []);

  /* =========================
     Démarrage/arrêt du scanner
     ========================= */
  const startScan = async () => {
    if (!videoRef.current) return;
    // Reset session
    setResult(null);
    lastCodeRef.current = "";
    cooldownRef.current = false;

    // Assure playsInline/autoplay/muted (iOS)
    videoRef.current.setAttribute("playsinline", "true");
    videoRef.current.setAttribute("muted", "true"); // empêche le blocage auto-play iOS
    videoRef.current.muted = true;

    // Démarre ZXing
    try {
      // stop éventuel avant relance
      await stopScan();

      controlsRef.current = await codeReaderRef.current.decodeFromVideoDevice(
        deviceId || undefined, // si "", ZXing choisira
        videoRef.current,
        (res, err, controls) => {
          if (!res) return; // rien lu sur cette frame
          const raw = res.getText ? res.getText() : String(res);
          const code = normalizeLicence(raw);
          if (!code) return;

          // anti spam multi-frames
          if (cooldownRef.current || code === lastCodeRef.current) return;
          lastCodeRef.current = code;
          cooldownRef.current = true;
          setTimeout(() => (cooldownRef.current = false), cooldownMs);

          try {
            navigator.vibrate?.(40);
            playBeep();
          } catch {}

          // Ferme la modale + vérifie la licence
          (async () => {
            await stopScan();
            setOpen(false);
            await runCheck(code);
          })();
        }
      );
    } catch (e) {
      console.error("Erreur startScan:", e);
      // Fallback: si deviceId échoue, retente sans id (laisser le browser choisir)
      try {
        await stopScan();
        controlsRef.current = await codeReaderRef.current.decodeFromVideoDevice(
          undefined,
          videoRef.current,
          (res) => {
            if (!res) return;
            const code = normalizeLicence(
              res.getText ? res.getText() : String(res)
            );
            if (!code) return;
            if (cooldownRef.current || code === lastCodeRef.current) return;
            lastCodeRef.current = code;
            cooldownRef.current = true;
            setTimeout(() => (cooldownRef.current = false), cooldownMs);
            try {
              navigator.vibrate?.(40);
              playBeep();
            } catch {}
            (async () => {
              await stopScan();
              setOpen(false);
              await runCheck(code);
            })();
          }
        );
      } catch (e2) {
        console.error("Erreur fallback start:", e2);
      }
    }
  };

  const stopScan = async () => {
    try {
      await controlsRef.current?.stop();
    } catch {}
    controlsRef.current = null;
    try {
      codeReaderRef.current?.reset?.();
    } catch {}
    // Arrêter les tracks vidéo si besoin
    try {
      const stream = videoRef.current?.srcObject;
      if (stream && typeof stream.getTracks === "function") {
        stream.getTracks().forEach((t) => t.stop());
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    } catch {}
  };

  // Ouverture/fermeture modale
  const openModal = () => {
    setModalKey((k) => k + 1); // force un nouveau <video>
    setOpen(true);
    // petit délai pour laisser React monter le <video>
    setTimeout(() => startScan(), 120);
  };
  const closeModal = async () => {
    await stopScan();
    setOpen(false);
  };

  // Changement de caméra en direct
  const handleSwitchDevice = async (id) => {
    setDeviceId(id);
    if (open) {
      await stopScan();
      setTimeout(() => startScan(), 120);
    }
  };

  /* =========================
     Check API
     ========================= */
  const runCheck = async (licenceCode) => {
    try {
      setChecking(true);
      const data = await checkLicencie({
        licence_number: licenceCode,
        saison: season,
      });
      setResult(data || null);
    } catch (e) {
      console.error(e);
      setResult({
        found: false,
        saison_ok: false,
        message: "Erreur lors de la vérification.",
      });
    } finally {
      setChecking(false);
    }
  };

  // Saisie manuelle
  const submitManual = async (e) => {
    e?.preventDefault?.();
    const code = normalizeLicence(manualCode);
    if (!code) return;
    await runCheck(code);
  };

  const statusBadge = useMemo(() => {
    if (!result) return null;
    const ok = !!result.saison_ok;
    return (
      <div
        className={`mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm
        ${
          ok
            ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200"
            : "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200"
        }`}
      >
        {ok ? `Licencié(e) ${season}` : `Non licencié pour ${season}`}
      </div>
    );
  }, [result, season]);

  return (
    <div className="min-h-screen pt-[100px] px-4 pb-10 font-satoshi bg-slate-50 dark:bg-slate-900">
      <div className="max-w-[1100px] mx-auto">
        <div className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 shadow-sm">
          <div className="px-5 py-4 bg-gradient-to-r from-indigo-600 via-blue-600 to-violet-600 text-white">
            <h1 className="text-xl md:text-2xl font-semibold">
              Scan licence-assurance France Judo
            </h1>
            <p className="text-white/80 text-sm mt-0.5">
              Scanne un code-barres (Code-128, EAN-13…) pour vérifier
              l’habilitation en {season}.
            </p>
          </div>

          {/* Toolbar */}
          <div className="p-4 bg-white dark:bg-slate-800/70">
            <div className="grid gap-3 md:grid-cols-[1fr,1fr,auto] items-end">
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  Saison
                </label>
                <select
                  value={season}
                  onChange={(e) => setSeason(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-700/60 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option>2025-2026</option>
                  <option>2024-2025</option>
                  <option>2023-2024</option>
                </select>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  Caméra
                </label>
                <select
                  value={deviceId}
                  onChange={(e) => handleSwitchDevice(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-700/60 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {devices.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || d.deviceId}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2 md:justify-end">
                <Button text="Scanner" onClick={openModal} className="px-5" />
                <Button
                  text="Réinitialiser"
                  onClick={() => setResult(null)}
                  className="px-5"
                />
              </div>
            </div>
          </div>

          {/* Cartouche résultat */}
          {result && (
            <div
              className={`mx-4 mb-4 p-4 rounded-xl ring-1 ${
                result.saison_ok
                  ? "bg-green-50 ring-green-200 dark:bg-green-900/20 dark:ring-green-800"
                  : "bg-red-50 ring-red-200 dark:bg-red-900/20 dark:ring-red-800"
              }`}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div>
                  <div className="text-base font-semibold">
                    {result.saison_ok
                      ? "Licencié(e) pour la saison sélectionnée"
                      : "Non licencié(e) pour la saison sélectionnée"}
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-300">
                    {result.message ||
                      (result.saison_ok
                        ? "Autorisé(e) en compétition."
                        : "Non autorisé(e) en compétition.")}
                  </div>
                  {result.licencie && (
                    <div className="mt-2 text-sm text-slate-700 dark:text-slate-200">
                      {result.licencie.prenom} {result.licencie.nom} —{" "}
                      {result.licencie.club} —{" "}
                      {result.licencie.genre === "M" ? "H" : "F"} — Licence:{" "}
                      <span className="font-mono">
                        {result.licencie.licence_number}
                      </span>
                      {result.licencie.saison && (
                        <>
                          {" "}
                          — Saison:{" "}
                          <span className="font-medium">
                            {result.licencie.saison}
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div>{statusBadge}</div>
              </div>
            </div>
          )}

          {/* Saisie manuelle */}
          <div className="p-4 bg-white dark:bg-slate-800/70">
            <form
              onSubmit={submitManual}
              className="grid gap-3 md:grid-cols-[1fr,auto]"
            >
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  Saisie manuelle (secours)
                </label>
                <input
                  type="text"
                  inputMode="text"
                  autoCapitalize="characters"
                  placeholder="Ex: M10071971VICTO01"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                  className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-700/60 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="flex items-end">
                <Button
                  text={checking ? "Vérification..." : "Vérifier"}
                  type="submit"
                  disabled={checking || !manualCode}
                  className="px-6"
                />
              </div>
            </form>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Astuce : bonne lumière, code parallèle au cadre, 15–25 cm de
              distance.
            </p>
          </div>
        </div>
      </div>

      {/* Modale Scanner */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-[720px] overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700">
            <div className="px-5 py-3 bg-slate-900 text-white flex items-center justify-between">
              <div className="font-semibold">Scanner un code-barres</div>
              <button
                onClick={closeModal}
                className="px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20"
              >
                Fermer
              </button>
            </div>

            <div className="p-4">
              <div className="text-xs text-slate-600 dark:text-slate-300 mb-2">
                Place le code au centre du cadre blanc.
              </div>

              <div className="relative w-full aspect-video rounded-2xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700 bg-black">
                {/* IMPORTANT: remount du <video> à chaque session via key */}
                <video
                  key={modalKey}
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  playsInline
                  muted
                  autoPlay
                />
                {/* Overlay */}
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="w-[78%] h-[34%] rounded-xl border-2 border-white/85 shadow-[0_0_0_9999px_rgba(0,0,0,0.28)]" />
                </div>
              </div>

              {/* Sélecteur caméra (utile sur desktop/multi-cam) */}
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  Caméra :
                  <select
                    value={deviceId}
                    onChange={(e) => handleSwitchDevice(e.target.value)}
                    className="ml-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-700/60 text-slate-900 dark:text-slate-100 px-2 py-1"
                  >
                    {devices.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label || d.deviceId}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="text-xs px-2 py-1 rounded-full bg-emerald-600 text-white">
                  Caméra active
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
